import { useState, useEffect } from 'react';
import logger from '@/utils/logger';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Plus, Edit, Trash2, Eye, EyeOff, Star, StarOff, 
  Loader2, Package2, ShoppingBag, Search, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Navbar from '@/components/Navbar';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDesigns() {
  const [designs, setDesigns] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDesignId, setDeleteDesignId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    checkAdminAndLoad();
  }, []);

  const checkAdminAndLoad = async () => {
    try {
      const response = await axios.get(`${API}/admin/designs`, { withCredentials: true });
      setDesigns(response.data || []);
      setIsAdmin(true);
      loadPurchases();
    } catch (error) {
      if (error.response?.status === 403) {
        setIsAdmin(false);
      } else {
        logger.error('Failed to load designs:', error);
        toast.error('Failed to load designs');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadPurchases = async () => {
    try {
      const response = await axios.get(`${API}/admin/design-purchases`, { withCredentials: true });
      setPurchases(response.data || []);
    } catch (error) {
      logger.error('Failed to load purchases:', error);
    }
  };

  const handleDelete = async () => {
    if (!deleteDesignId) return;

    try {
      await axios.delete(`${API}/admin/designs/${deleteDesignId}`, { withCredentials: true });
      toast.success('Design deleted successfully');
      setDeleteDesignId(null);
      checkAdminAndLoad();
    } catch (error) {
      logger.error('Failed to delete design:', error);
      toast.error('Failed to delete design');
    }
  };

  const togglePublished = async (design) => {
    try {
      await axios.put(
        `${API}/admin/designs/${design.design_id}`,
        { is_published: !design.is_published },
        { withCredentials: true }
      );
      toast.success(design.is_published ? 'Design unpublished' : 'Design published');
      checkAdminAndLoad();
    } catch (error) {
      logger.error('Failed to update design:', error);
      toast.error('Failed to update design');
    }
  };

  const toggleFeatured = async (design) => {
    try {
      await axios.put(
        `${API}/admin/designs/${design.design_id}`,
        { is_featured: !design.is_featured },
        { withCredentials: true }
      );
      toast.success(design.is_featured ? 'Removed from featured' : 'Added to featured');
      checkAdminAndLoad();
    } catch (error) {
      logger.error('Failed to update design:', error);
      toast.error('Failed to update design');
    }
  };

  const filteredDesigns = designs.filter(d =>
    d.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (isAdmin === false) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
            <h1 className="text-2xl font-heading font-bold mb-2">Access Denied</h1>
            <p className="text-muted-foreground mb-6">
              You don&apos;t have permission to access the admin area.
            </p>
            <Button onClick={() => navigate('/dashboard')} className="rounded-full">
              Go to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-heading font-extrabold tracking-tight mb-2">
              Design Management
            </h1>
            <p className="text-muted-foreground">
              Create and manage your crochet patterns
            </p>
          </div>
          <Button
            onClick={() => navigate('/admin/designs/new')}
            className="rounded-full bg-primary text-primary-foreground shadow-lg"
          >
            <Plus className="h-5 w-5 mr-2" />
            New Design
          </Button>
        </div>

        <Tabs defaultValue="designs" className="space-y-6">
          <TabsList className="rounded-full bg-stone-100 p-1">
            <TabsTrigger value="designs" className="rounded-full">
              <Package2 className="h-4 w-4 mr-2" />
              Designs ({designs.length})
            </TabsTrigger>
            <TabsTrigger value="purchases" className="rounded-full">
              <ShoppingBag className="h-4 w-4 mr-2" />
              Purchases ({purchases.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="designs">
            {/* Search */}
            <div className="relative max-w-md mb-6">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search designs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 rounded-xl"
              />
            </div>

            {/* Designs Table */}
            {filteredDesigns.length === 0 ? (
              <div className="text-center py-16 rounded-3xl bg-white border border-stone-100">
                <Package2 className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="text-xl font-heading font-semibold mb-2">No designs yet</h3>
                <p className="text-muted-foreground mb-6">
                  Create your first crochet design to get started.
                </p>
                <Button
                  onClick={() => navigate('/admin/designs/new')}
                  className="rounded-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Design
                </Button>
              </div>
            ) : (
              <div className="rounded-2xl bg-white border border-stone-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-stone-50 border-b border-stone-100">
                      <tr>
                        <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">Design</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">Category</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">Price</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">Status</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">Stats</th>
                        <th className="text-right px-6 py-4 text-sm font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {filteredDesigns.map((design) => (
                        <tr key={design.design_id} className="hover:bg-stone-50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-3">
                              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-rose-100 to-orange-100 flex items-center justify-center overflow-hidden">
                                {design.images && design.images.length > 0 ? (
                                  <img
                                    src={design.images[0].url}
                                    alt=""
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <Package2 className="h-6 w-6 text-primary" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium">{design.title}</p>
                                <p className="text-xs text-muted-foreground">{design.difficulty}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="capitalize text-sm">{design.category}</span>
                          </td>
                          <td className="px-6 py-4">
                            {design.is_free ? (
                              <span className="px-2 py-1 rounded-full bg-green-100 text-green-700 text-sm font-medium">
                                Free
                              </span>
                            ) : (
                              <span className="font-medium">€{design.price}</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                design.is_published 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-stone-100 text-stone-600'
                              }`}>
                                {design.is_published ? 'Published' : 'Draft'}
                              </span>
                              {design.is_featured && (
                                <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-muted-foreground">
                              <p>{design.purchase_count || 0} sales</p>
                              <p>{design.download_count || 0} downloads</p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-end space-x-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => togglePublished(design)}
                                title={design.is_published ? 'Unpublish' : 'Publish'}
                                className="h-8 w-8 p-0"
                              >
                                {design.is_published ? (
                                  <EyeOff className="h-4 w-4 text-stone-500" />
                                ) : (
                                  <Eye className="h-4 w-4 text-stone-500" />
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => toggleFeatured(design)}
                                title={design.is_featured ? 'Remove from featured' : 'Add to featured'}
                                className="h-8 w-8 p-0"
                              >
                                {design.is_featured ? (
                                  <StarOff className="h-4 w-4 text-yellow-500" />
                                ) : (
                                  <Star className="h-4 w-4 text-stone-500" />
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => navigate(`/admin/designs/${design.design_id}/edit`)}
                                className="h-8 w-8 p-0"
                              >
                                <Edit className="h-4 w-4 text-stone-500" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setDeleteDesignId(design.design_id)}
                                className="h-8 w-8 p-0 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4 text-stone-500 hover:text-destructive" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="purchases">
            {purchases.length === 0 ? (
              <div className="text-center py-16 rounded-3xl bg-white border border-stone-100">
                <ShoppingBag className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="text-xl font-heading font-semibold mb-2">No purchases yet</h3>
                <p className="text-muted-foreground">
                  Purchases will appear here once customers buy your designs.
                </p>
              </div>
            ) : (
              <div className="rounded-2xl bg-white border border-stone-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-stone-50 border-b border-stone-100">
                      <tr>
                        <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">Date</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">Customer</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">Design</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">Amount</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {purchases.map((purchase) => {
                        const design = designs.find(d => d.design_id === purchase.design_id);
                        return (
                          <tr key={purchase.purchase_id} className="hover:bg-stone-50 transition-colors">
                            <td className="px-6 py-4 text-sm">
                              {new Date(purchase.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 text-sm">
                              {purchase.email}
                            </td>
                            <td className="px-6 py-4 text-sm font-medium">
                              {design?.title || purchase.design_id}
                            </td>
                            <td className="px-6 py-4 text-sm font-medium">
                              €{purchase.amount}
                            </td>
                            <td className="px-6 py-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                purchase.payment_status === 'paid'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-yellow-100 text-yellow-700'
                              }`}>
                                {purchase.payment_status}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteDesignId} onOpenChange={() => setDeleteDesignId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Design</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this design? This action cannot be undone.
              Customers who purchased this design will lose access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
